/* eslint-disable @typescript-eslint/naming-convention */
import type { CartItem } from '@sola/schema/dotcom/cart/cart-item.ts';
import { CartItemLegacyGroupEnum } from '@sola/schema/dotcom/cart/cart-item.ts';
import type { Cart } from '@sola/schema/dotcom/cart/cart.ts';
import type {
  CheckoutBillingInfo,
  CheckoutPaymentTypeEnum,
} from '@sola/schema/dotcom/checkout/billingInfo.ts';
import type { CheckoutContactInfo } from '@sola/schema/dotcom/checkout/contactInfo.ts';
import { CURRENCY_USD } from '@sola/shared-utils/constants.ts';

import { OptimizelyEventKey } from '../optimizely-schemas.ts';
import type { AnalyticsCartItem } from './mappings.ts';
import { mapAnalyticsCartItems } from './mappings.ts';
import { trackEvent, trackOptimizelyEvent } from './transport.ts';

const events = {
  cartAdd: 'add_to_cart',
  cartAddPaymentInfo: 'add_payment_info',
  cartAddShippingInfo: 'add_shipping_info',
  cartRemove: 'remove_from_cart',
  cartView: 'view_cart',
  checkoutBegin: 'begin_checkout',
  checkoutCommitmentPayment: 'commitment_payment',
  checkoutFundCommitment: 'fund_commitment',
  checkoutFundDonation: 'fund_donation',
  checkoutGiftPayment: 'gift_payment',
  checkoutSuccess: 'purchase',
  rateSelected: 'rate_selected',
};

export const trackCartAdd = (addedItems: CartItem[], cartId: Cart['id']) => {
  if (!addedItems?.length) {
    return;
  }

  const cartItems = mapAnalyticsCartItems(addedItems);
  let value = 0;

  for (const item of cartItems) {
    value += item.price * item.quantity;
  }

  trackEvent({
    event: events.cartAdd,
    items: cartItems,
    currency: CURRENCY_USD,
    transaction_id: cartId,
    value,
  });
};

/**
 * Derive the add-to-cart category from the current URL pathname.
 * /catalog/ → "Gift Catalog", otherwise → "Fund".
 */
const deriveCategoryFromPath = (pathname?: string): string => {
  if (pathname?.includes('/catalog/')) {
    return 'Gift Catalog';
  }
  return 'Fund';
};

/**
 * Fire an Optimizely conversion event for an add-to-cart action.
 * Encapsulates the event key and property names so callers only need to
 * provide the product-specific values.
 *
 * If `category` is omitted, it is derived from the current URL path:
 *   /catalog/ → "Gift Catalog", otherwise → "Fund".
 */
export const trackOptimizelyCartAdd = (params: {
  productName: string;
  category?: string;
  productId: string;
  /** Revenue in dollars (e.g. 38 or 38.50) — converted to cents internally */
  revenue: number;
  /** Quantity added; defaults to 1 */
  quantity?: number;
}) => {
  const category =
    params.category ?? deriveCategoryFromPath(globalThis.location?.pathname);

  // Return once hashing completes and fetch is dispatched. Checkout callers can
  // await this without waiting for the network response.
  return trackOptimizelyEvent({
    eventKey: OptimizelyEventKey.enum.add_to_carts,
    url: globalThis.location?.href || '',
    properties: {
      'Product Name': params.productName,
      Category: category,
      'Product ID': params.productId,
    },
    revenue: Math.round(params.revenue * 100),
    value: params.quantity ?? 1,
  });
};

export const trackCartRemove = (removedItems: CartItem[]) => {
  if (!removedItems || !removedItems.length) {
    return;
  }

  const cartItems = mapAnalyticsCartItems(removedItems);
  let value = 0;

  for (const item of cartItems) {
    value += item.price * item.quantity;
  }

  trackEvent({
    event: events.cartRemove,
    items: cartItems,
    currency: CURRENCY_USD,
    value,
  });
};

export const trackCartView = (cart: Cart) => {
  const cartItems = mapAnalyticsCartItems(cart.items);
  trackEvent({
    event: events.cartView,
    items: cartItems,
    currency: CURRENCY_USD,
    transaction_id: cart.id,
    value: cart.totalToday,
  });
};

/**
 * Fire a GA4 custom event and Optimizely conversion event when the user
 * selects a sponsorship rate (standard vs. introductory).
 *
 * Feeds:
 * - GA4 `rate_selected` event for funnel/abandonment segmentation
 * - Optimizely `rate_selection` conversion for A/B test metrics
 */
export const trackRateSelection = (params: {
  selectedRate: 'standard' | 'introductory';
  rateAmount: number;
  childId: string;
}) => {
  trackEvent({
    event: events.rateSelected,
    rate_type: params.selectedRate,
    rate_amount: params.rateAmount,
    child_id: params.childId,
  });

  trackOptimizelyEvent({
    eventKey: OptimizelyEventKey.enum.rate_selection,
    url: globalThis.location?.href || '',
    properties: {
      'Rate Type': params.selectedRate,
      'Rate Amount': String(params.rateAmount),
      'Child ID': params.childId,
    },
  });
};

export const trackCheckoutBegin = (
  cart: Cart,
  rateType?: 'standard' | 'introductory',
) => {
  const cartItems = mapAnalyticsCartItems(cart.items);
  const offerDualRate = cart.campaign?.offerDualRate ?? false;
  trackEvent({
    event: events.checkoutBegin,
    items: cartItems,
    currency: CURRENCY_USD,
    transaction_id: cart.id,
    value: cart.totalToday,
    ...(rateType && { rate_type: rateType }),
    ...(offerDualRate !== undefined && {
      offer_dual_rate: offerDualRate,
    }),
  });
};

const getCheckoutEventKey = (item: AnalyticsCartItem): string | undefined => {
  switch (item.item_category) {
    case CartItemLegacyGroupEnum.CommitmentPayment: {
      return events.checkoutCommitmentPayment;
    }
    case CartItemLegacyGroupEnum.Fund: {
      return item.item_variant === 'OneTime'
        ? events.checkoutFundDonation
        : events.checkoutFundCommitment;
    }
    case CartItemLegacyGroupEnum.GiftPayment: {
      return events.checkoutGiftPayment;
    }
  }
};

const trackCheckoutSuccessKey = (item: AnalyticsCartItem, cartId: string) => {
  const event = getCheckoutEventKey(item);
  if (!event) {
    return;
  }

  trackEvent({
    event,
    items: [item],
    currency: CURRENCY_USD,
    transaction_id: cartId,
    value: item.price * item.quantity,
  });
};

export const trackCheckoutSuccess = (
  cart: Cart,
  rateType?: 'standard' | 'introductory',
) => {
  const cartItems = mapAnalyticsCartItems(cart.items);

  if (!cartItems.length) {
    return;
  }

  const data = {
    event: events.checkoutSuccess,
    items: cartItems,
    currency: CURRENCY_USD,
    transaction_id: cart.id,
    value: cart.totalToday,
    sponsorships: 0,
    fund_commitments: 0,
    fund_donations: 0,
    gift_payments: 0,
    commitment_payments: 0,
    ...(rateType && { rate_type: rateType }),
  };

  for (const item of cartItems) {
    trackCheckoutSuccessKey(item, cart.id);

    switch (item.item_category) {
      case CartItemLegacyGroupEnum.ChildSelected:
      case CartItemLegacyGroupEnum.FirstPayment:
      case CartItemLegacyGroupEnum.EPayment: {
        data.sponsorships += 1;
        break;
      }
      case CartItemLegacyGroupEnum.CommitmentPayment: {
        data.commitment_payments += 1;
        break;
      }
      case CartItemLegacyGroupEnum.Fund: {
        if (item.item_variant === 'OneTime') {
          data.fund_donations += 1;
        } else {
          data.fund_commitments += 1;
        }
        break;
      }
      case CartItemLegacyGroupEnum.GiftPayment: {
        data.gift_payments += 1;
        break;
      }
    }
  }

  trackEvent(data);
};

export const trackCheckoutAddPaymentInfo = (
  paymentType: CheckoutPaymentTypeEnum,
  cart: Cart,
  details?: {
    paymentTypeClass?: ApplePayJS.ApplePayPaymentMethodSelectedEvent['paymentMethod']['type'];
  },
) => {
  trackEvent({
    event: events.cartAddPaymentInfo,
    currency: CURRENCY_USD,
    payment_type: paymentType,
    value: cart.totalToday,
    items: mapAnalyticsCartItems(cart.items),
    ...(details?.paymentTypeClass && {
      payment_type_class: details.paymentTypeClass,
    }),
  });
};

export const trackCheckoutAddShippingInfo = (
  cart: Cart,
  shippingInfo?: {
    city?: CheckoutBillingInfo['billingCity'];
    country?: CheckoutBillingInfo['billingCountry'] | string;
    firstName?: CheckoutContactInfo['firstName'];
    lastName?: CheckoutContactInfo['lastName'];
    phone?: CheckoutContactInfo['phone'];
    state?: CheckoutBillingInfo['billingState'];
    zip?: CheckoutBillingInfo['billingPostal'];
  },
) => {
  trackEvent({
    event: events.cartAddShippingInfo,
    currency: CURRENCY_USD,
    value: cart.totalToday,
    items: mapAnalyticsCartItems(cart.items),
    supporter_city: shippingInfo?.city,
    supporter_country: shippingInfo?.country,
    supporter_first_name: shippingInfo?.firstName,
    supporter_last_name: shippingInfo?.lastName,
    supporter_phone: shippingInfo?.phone,
    supporter_state: shippingInfo?.state,
    supporter_zip: shippingInfo?.zip,
  });
};
